/**
 * Tournaments Only Layout Component
 * Renders only the tournaments section when campaignOnly: { type: 'tournaments', campaignID?: string } is set.
 * - If campaignID is provided, navigates carousel to that specific tournament
 * - If campaignID is not found or not provided, falls back to first tournament
 *
 * Minimal layout with just tournaments content and tournament navigation
 */

import React, { useCallback, memo } from 'react';
import { SECTION_NAMES } from '@repo/common';

import {
  useCampaigns,
  useCampaignActions,
  useCampaignsStore,
  TOURNAMENTS_CAMPAIGN_TYPE,
  CampaignTypeProvider,
} from 'features/campaigns';
import { ViewSwitcher } from 'features/navigation';

import { useAppStore } from '../../../store/app/appStore';
import { useCampaignSelection } from '../hooks/useCampaignSelection';

import styles from '../styles/CampaignOnlyLayout.module.css';

interface TournamentsOnlyLayoutProps {
  campaignID?: string;
}

const TournamentsOnlyLayout: React.FC<TournamentsOnlyLayoutProps> = ({ campaignID }) => {
  // Get settings from app store
  const campaignOnly = useAppStore((state) => state.settings.campaignOnly);

  // Show layout if campaignOnly.type === TOURNAMENTS_CAMPAIGN_TYPE
  const showTournamentsOnly = campaignOnly?.type === TOURNAMENTS_CAMPAIGN_TYPE;

  // Get tournament state from the campaigns store (already sorted)
  const activeTournamentId = useCampaignsStore((state) => state.getSelectedCampaignId(TOURNAMENTS_CAMPAIGN_TYPE));
  const tournaments = useCampaigns(TOURNAMENTS_CAMPAIGN_TYPE) as { id: string }[];
  const { setSelectedCampaignId } = useCampaignActions();
  const setActiveTournament = useCallback(
    (id: string) => setSelectedCampaignId(id, TOURNAMENTS_CAMPAIGN_TYPE),
    [setSelectedCampaignId],
  );

  // Use shared campaign selection hook
  useCampaignSelection({
    shouldShow: showTournamentsOnly,
    campaignID,
    items: tournaments,
    activeItemId: activeTournamentId ?? undefined,
    setActiveItem: setActiveTournament,
    sectionName: SECTION_NAMES.TOURNAMENTS,
    debugPrefix: 'TournamentsOnly',
    getItemId: (tournament) => tournament.id,
  });

  // Don't render anything if campaignOnly.type !== TOURNAMENTS_CAMPAIGN_TYPE
  if (!showTournamentsOnly) {
    return null;
  }

  return (
    <CampaignTypeProvider type={TOURNAMENTS_CAMPAIGN_TYPE}>
      <div className={styles.campaignOnlyContainer}>
        <div className={styles.contentContainer}>
          {/* Tournaments content via ViewSwitcher — tab navigation is handled by CampaignTabs */}
          <ViewSwitcher showTournamentNavigation={!!activeTournamentId} campaingOnlyView={true}/>
        </div>
      </div>
    </CampaignTypeProvider>
  );
};

export default memo(TournamentsOnlyLayout);